import {
  metersPerDegree,
  Point,
  projectPolygonOntoGlobe,
} from "./projectPolygonOntoGlobe";
import { Polygon, Position } from "geojson";

// caps miter joins at sharp corners so a near-reversal doesn't spike outward
const MAX_MITER_SCALE = 4;

/** Widths either side of a path, relative to its direction of travel. */
export interface BoxSides {
  leftM: number;
  rightM: number;
}

export const symmetricSides = (widthM: number): BoxSides => ({
  leftM: widthM / 2,
  rightM: widthM / 2,
});

/**
 * Polygon `leftM`/`rightM` either side of a [lng, lat] path with flat ends and
 * mitered corners, in a local projection accurate over task-sized paths.
 * Undefined for fewer than two distinct points.
 */
export const lineToBox = (
  coordinates: Position[],
  { leftM, rightM }: BoxSides
): Polygon | undefined => {
  // (0, 0) is an unset point, not a location
  const points = coordinates.flatMap(([lng, lat]): Point[] =>
    lng === undefined || lat === undefined || (lng === 0 && lat === 0)
      ? []
      : [[lng, lat]]
  );
  const origin = points[0];
  if (!origin) {
    return;
  }
  const [lng0, lat0] = origin;
  const metersPer = metersPerDegree(lat0);

  // project to local meters, dropping zero-length segments
  const xs: number[] = [];
  const ys: number[] = [];
  for (const [lng, lat] of points) {
    const x = (lng - lng0) * metersPer.lng;
    const y = (lat - lat0) * metersPer.lat;
    const last = xs.length - 1;
    if (last >= 0 && xs[last] === x && ys[last] === y) {
      continue;
    }
    xs.push(x);
    ys.push(y);
  }
  const n = xs.length;
  if (n < 2) {
    return;
  }

  // unit left-hand normal of each segment
  const nxs = new Float64Array(n - 1);
  const nys = new Float64Array(n - 1);
  for (let i = 0; i < n - 1; i++) {
    const dx = xs[i + 1]! - xs[i]!;
    const dy = ys[i + 1]! - ys[i]!;
    const length = Math.hypot(dx, dy);
    nxs[i] = -dy / length;
    nys[i] = dx / length;
  }

  const left: Point[] = [];
  const right: Point[] = [];
  for (let i = 0; i < n; i++) {
    // ends use their segment's normal, which makes them flat
    const prev = Math.max(i - 1, 0);
    const next = Math.min(i, n - 2);
    let mx = nxs[prev]! + nxs[next]!;
    let my = nys[prev]! + nys[next]!;
    const mLength = Math.hypot(mx, my);
    let scale = 1;
    if (mLength < 1e-9) {
      // segments reverse onto each other; fall back to the outgoing normal
      mx = nxs[next]!;
      my = nys[next]!;
    } else {
      mx /= mLength;
      my /= mLength;
      const cos = mx * nxs[next]! + my * nys[next]!;
      scale *= Math.min(1 / cos, MAX_MITER_SCALE);
    }
    const x = xs[i]!;
    const y = ys[i]!;
    left.push([x + mx * scale * leftM, y + my * scale * leftM]);
    right.push([x - mx * scale * rightM, y - my * scale * rightM]);
  }

  right.reverse();
  return {
    type: "Polygon",
    coordinates: [
      projectPolygonOntoGlobe([...left, ...right, left[0]!], 0, lat0, lng0),
    ],
  };
};
